import { useMemo, useState } from 'react';
import PreferEarlierTick from '@/components/booking/PreferEarlierTick';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { AWAITING_CLIENT, generateSelfBookLink } from '@/domain/depositFlow';
import type { Channel, CustomerRecord, SessionLength } from '@/domain/types';
import { customerName, aud } from '@/utils/format';
import { depositForSession } from '@/domain/pricing';
import { addDays } from 'date-fns';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

const STEPS = ['Artist', 'Session length', 'Date range', 'Deposit', 'Preview and send'] as const;

export default function SelfBookLinkGenerator() {
  const navigate = useNavigate();
  const { state } = useLocation() as { state: { customerId?: string } | null };
  const customers = useBrainStore((s) => s.customers);
  const artists = useBrainStore((s) => s.artists);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const initialCustomerId = state?.customerId ?? Object.keys(customers).find((id) => id !== AWAITING_CLIENT) ?? '';
  const [customerId, setCustomerId] = useState(initialCustomerId);
  const customer: CustomerRecord | undefined = customers[customerId];
  // Real clients only: never the placeholder a held slot waits under (B5).
  const pickableCustomers = useMemo(
    () => Object.values(customers).filter((c) => c.id !== AWAITING_CLIENT),
    [customers],
  );

  const [step, setStep] = useState(0);
  // The signed-in artist sends their own link (B5); staff default to Pat.
  const sessionArtistId = useSessionStore((s) => s.artistId);
  const [artistId, setArtistId] = useState<string>(
    sessionArtistId && artists[sessionArtistId] ? sessionArtistId : 'pat',
  );
  const [sessionLength, setSessionLength] = useState<SessionLength>(
    customer?.designIntent?.size ?? 'half-day',
  );
  const [rangeDays, setRangeDays] = useState(14);
  const [depositAud, setDepositAud] = useState<number>(
    depositForSession(sessionLength, artists[artistId]),
  );
  const [channel, setChannel] = useState<Channel>(customer?.conversation[customer.conversation.length - 1]?.channel ?? 'sms');
  const [sentToken, setSentToken] = useState<string | null>(null);
  // B7 (8xat D08-09; Anas 1 Oct Q5): staff tick it for the client on links they make.
  const [preferEarlier, setPreferEarlier] = useState(false);

  if (!customer) {
    return (
      <section className="px-8 py-12 max-w-3xl">
        <h1 className="heading-display text-3xl text-cream">Pick a customer</h1>
        <p className="font-serif text-cream-soft mt-3">
          Self-book links are generated by the artist taking the piece. Pick the client below, or
          start from the artist&apos;s dashboard.
        </p>
        <select
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          data-testid="self-book-pick-customer"
          className="mt-4 bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
        >
          {pickableCustomers.map((c) => (
            <option key={c.id} value={c.id}>
              {customerName(c)}
            </option>
          ))}
        </select>
      </section>
    );
  }

  if (sentToken) {
    return (
      <section className="px-8 py-12 max-w-2xl" data-testid="self-book-sent">
        <CheckCircle2 size={32} className="text-mint" />
        <h1 className="heading-display text-3xl text-cream mt-4">Link sent</h1>
        <p className="font-serif text-cream-soft mt-2">
          {customerName(customer)} just received their self-book link via {channel}. They have
          48 hours.
        </p>
        <p className="font-mono text-xs text-cream-soft mt-4 break-all">
          book.tattoovault.com.au/s/{sentToken}
        </p>
        <div className="flex gap-2 mt-6">
          <button
            type="button"
            onClick={() => navigate(`/customer-book/${sentToken}`)}
            className="btn-primary text-xs"
          >
            Preview customer-facing page
          </button>
          <button
            type="button"
            onClick={() => navigate(`/customer/${customer.id}`)}
            className="btn-ghost text-xs"
          >
            Back to record
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="px-8 py-8 max-w-3xl" data-testid="self-book-generator">
      <header>
        <p className="label-small-caps text-gold-bright">PRD §12.11</p>
        <h1 className="heading-display text-2xl text-cream mt-2">
          Self-book link for {customerName(customer)}
        </h1>
        {/* B5: the artist opens this from the side menu, so they pick the client here. */}
        <label className="mt-3 flex items-center gap-2 font-inter text-xs text-cream-soft">
          Client
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            data-testid="self-book-pick-customer"
            className="bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
          >
            {pickableCustomers.map((c) => (
              <option key={c.id} value={c.id}>
                {customerName(c)}
              </option>
            ))}
          </select>
        </label>
      </header>

      <ol className="mt-6 flex gap-2">
        {STEPS.map((s, idx) => (
          <li key={s} className="flex-1">
            <button
              type="button"
              onClick={() => setStep(idx)}
              className={clsx(
                'w-full text-left rounded-md px-3 py-2 text-xs',
                idx === step
                  ? 'border border-gold-bright bg-gold/10 text-gold-bright'
                  : 'border border-gold-dim/25 text-cream-soft',
              )}
            >
              <p className="font-inter text-[10px] uppercase tracking-widest">Step {idx + 1}</p>
              <p className="mt-0.5">{s}</p>
            </button>
          </li>
        ))}
      </ol>

      <div className="mt-6 panel p-5 space-y-4">
        {step === 0 ? (
          <div>
            <h3 className="heading-serif text-sm text-cream mb-2">Pick artist</h3>
            <div className="grid grid-cols-2 gap-2">
              {Object.values(artists).map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setArtistId(a.id)}
                  className={clsx(
                    'rounded-md border px-3 py-2 text-left text-sm',
                    artistId === a.id
                      ? 'border-gold-bright bg-gold/10 text-gold-bright'
                      : 'border-gold-dim/30 text-cream',
                  )}
                >
                  <p className="font-inter">{a.displayName}</p>
                  <p className="font-inter text-[10px] text-cream-soft uppercase mt-0.5">
                    {a.styleSpeciality.join(', ')}
                  </p>
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {step === 1 ? (
          <div>
            <h3 className="heading-serif text-sm text-cream mb-2">Session length</h3>
            <div className="grid grid-cols-4 gap-2">
              {(['small', 'half-day', 'full-day', 'multi-session'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setSessionLength(s);
                    setDepositAud(depositForSession(s, artists[artistId]));
                  }}
                  className={clsx(
                    'rounded-md border px-3 py-3 text-center text-xs',
                    sessionLength === s
                      ? 'border-gold-bright bg-gold/10 text-gold-bright'
                      : 'border-gold-dim/30 text-cream',
                  )}
                >
                  <p className="font-inter capitalize">{s.replace('-', ' ')}</p>
                  <p className="font-mono text-[10px] mt-1">
                    {aud(depositForSession(s, artists[artistId]))} deposit
                  </p>
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {step === 2 ? (
          <div>
            <h3 className="heading-serif text-sm text-cream mb-2">Date range</h3>
            <div className="grid grid-cols-3 gap-2">
              {[
                [14, 'Next 2 weeks'],
                [30, 'Next month'],
                [60, 'Next 2 months'],
              ].map(([days, label]) => (
                <button
                  key={days as number}
                  type="button"
                  onClick={() => setRangeDays(days as number)}
                  className={clsx(
                    'rounded-md border px-3 py-3 text-center text-xs',
                    rangeDays === (days as number)
                      ? 'border-gold-bright bg-gold/10 text-gold-bright'
                      : 'border-gold-dim/30 text-cream',
                  )}
                >
                  <p className="font-inter">{label as string}</p>
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {step === 3 ? (
          <div>
            <h3 className="heading-serif text-sm text-cream mb-2">Deposit amount</h3>
            <input
              type="number"
              min={50}
              step={50}
              value={depositAud}
              onChange={(e) => setDepositAud(parseInt(e.target.value || '0', 10))}
              className="w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-cream font-cinzel text-xl"
              data-testid="deposit-amount-input"
            />
            <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-2">
              Default for {sessionLength} is {aud(depositForSession(sessionLength, artists[artistId]))}.
            </p>
          </div>
        ) : null}
        {step === 4 ? (
          <div>
            <h3 className="heading-serif text-sm text-cream mb-2">Send</h3>
            <div className="panel-dark p-4 text-xs space-y-2">
              <p>
                <span className="font-inter text-cream-soft uppercase">Customer</span>{' '}
                <span className="font-inter text-cream">{customerName(customer)}</span>
              </p>
              <p>
                <span className="font-inter text-cream-soft uppercase">Artist</span>{' '}
                <span className="font-inter text-cream">{artists[artistId]?.displayName}</span>
              </p>
              <p>
                <span className="font-inter text-cream-soft uppercase">Session</span>{' '}
                <span className="font-inter text-cream">{sessionLength}</span>
              </p>
              <p>
                <span className="font-inter text-cream-soft uppercase">Deposit</span>{' '}
                <span className="font-inter text-gold-bright">{aud(depositAud)}</span>
              </p>
              <p>
                <span className="font-inter text-cream-soft uppercase">Channel</span>{' '}
                <select
                  value={channel}
                  onChange={(e) => setChannel(e.target.value as Channel)}
                  className="ml-2 bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1 text-cream"
                >
                  <option value="sms">SMS</option>
                  <option value="ig-dm">Instagram DM</option>
                  <option value="meta-dm">Meta DM</option>
                  <option value="email">Email</option>
                </select>
              </p>
              <p>
                <span className="font-inter text-cream-soft uppercase">Date range</span>{' '}
                <span className="font-inter text-cream">Next {rangeDays} days</span>
              </p>
              <PreferEarlierTick checked={preferEarlier} onChange={setPreferEarlier} className="mt-2" />
            </div>
            <button
              type="button"
              className="btn-primary mt-4 w-full"
              data-testid="send-self-book-link"
              onClick={() => {
                const link = generateSelfBookLink({
                  customerId: customer.id,
                  artistId,
                  // The link is the artist's: it books under their studio (B7).
                  studioId: artists[artistId]?.studioId ?? customer.primaryStudioId,
                  sessionLength,
                  depositAud,
                  dateRangeStart: clockIso,
                  dateRangeEnd: addDays(new Date(clockIso), rangeDays).toISOString(),
                  expiresAt: addDays(new Date(clockIso), 2).toISOString(),
                  channelSent: channel,
                  ...(preferEarlier ? { preferBroughtForward: true } : {}),
                });
                setSentToken(link.token);
              }}
            >
              Send via {channel}
            </button>
          </div>
        ) : null}

        <div className="flex justify-between pt-4 border-t border-gold-dim/15">
          <button
            type="button"
            data-testid="wizard-back"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="btn-quiet"
          >
            <ChevronLeft size={12} /> Back
          </button>
          <button
            type="button"
            data-testid="wizard-next"
            disabled={step === STEPS.length - 1}
            onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
            className="btn-quiet"
          >
            Next <ChevronRight size={12} />
          </button>
        </div>
      </div>
    </section>
  );
}
